/**
 * Disaster Recovery Context
 * This context manages the disaster recovery mode state across the application to display global EAP phone numbers and error messages during outages
 *
 * This context can be used in both React components and non-React code.
 * It provides a way to set and get the disaster recovery mode state globally with global ref
 * The Context + Ref Pattern is used to allow both React components and non-React code to access the same context value.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import queryString from 'query-string';

interface DisasterRecoveryContextType {
  isInDisasterRecoveryMode: boolean;
  setIsInDisasterRecoveryMode: (value: boolean) => void;
}

const DisasterRecoveryContext = createContext<DisasterRecoveryContextType>({
  isInDisasterRecoveryMode: false,
  setIsInDisasterRecoveryMode: () => {},
});

// Global ref to store the context value
let disasterRecoveryContextRef: DisasterRecoveryContextType | null = null;

export const DisasterRecoveryProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isInDisasterRecoveryMode, setIsInDisasterRecoveryMode] =
    useState(false);

  const contextValue = useMemo(
    () => ({
      isInDisasterRecoveryMode,
      setIsInDisasterRecoveryMode,
    }),
    [isInDisasterRecoveryMode],
  );

  useEffect(() => {
    // Update the global ref whenever the context value changes
    disasterRecoveryContextRef = contextValue;

    return () => {
      disasterRecoveryContextRef = null;
    };
  }, [contextValue]);

  // Check URL params and pending disaster recovery mode on mount
  useEffect(() => {
    const query = queryString.parse(location.search);
    if (query.error === 'server_error') {
      setIsInDisasterRecoveryMode(true);
    }

    // Check if there's a pending disaster recovery mode from before the provider was mounted
    //
    // TIMING ISSUE SOLUTION: pendingDisasterRecoveryMode handles a race condition in the USL authentication flow
    //
    // Problem: In run-with-usl.tsx, AuthEvents listeners are set up to call setDisasterRecoveryMode(true)
    // when a 500 error occurs during token exchange. However, these listeners are active BEFORE the React
    // app renders and the DisasterRecoveryProvider mounts. This creates a timing issue where:
    // 1. USL authentication starts and sets up AuthEvents listeners
    // 2. A 500 error occurs during token exchange, triggering setDisasterRecoveryMode(true)
    // 3. setDisasterRecoveryMode() tries to access the context, but DisasterRecoveryProvider isn't mounted yet
    // 4. This results in "DisasterRecoveryProvider not mounted or context not available" error
    //
    // Solution: When setDisasterRecoveryMode() is called before the provider is mounted:
    // 1. The fallback mechanism catches the "not mounted" error
    // 2. Instead of crashing, it stores the disaster recovery state in sessionStorage as 'pendingDisasterRecoveryMode'
    // 3. When the DisasterRecoveryProvider finally mounts, this useEffect checks for any pending state
    // 4. If found, it applies the pending disaster recovery mode and cleans up the temporary storage
    //
    // This ensures that disaster recovery mode is properly activated even when the timing of authentication
    // errors occurs before React components are fully initialized, maintaining the user experience without crashes.
    const pendingMode = sessionStorage.getItem('pendingDisasterRecoveryMode');
    if (pendingMode !== null) {
      setIsInDisasterRecoveryMode(pendingMode === 'true');
      // Clear the pending mode after applying it
      sessionStorage.removeItem('pendingDisasterRecoveryMode');
    }
  }, []);

  return (
    <DisasterRecoveryContext.Provider value={contextValue}>
      {children}
    </DisasterRecoveryContext.Provider>
  );
};

// Custom hook for React components
export const useDisasterRecovery = () => {
  const context = useContext(DisasterRecoveryContext);
  if (!context) {
    throw new Error(
      'useDisasterRecovery must be used within DisasterRecoveryProvider',
    );
  }
  return context;
};

// Function for non-React code
export const getDisasterRecoveryContext = () => {
  if (!disasterRecoveryContextRef) {
    throw new Error(
      'DisasterRecoveryProvider not mounted or context not available',
    );
  }
  return disasterRecoveryContextRef;
};

// Convenience function for non-React code
export const setDisasterRecoveryMode = (value: boolean) => {
  try {
    const context = getDisasterRecoveryContext();
    context.setIsInDisasterRecoveryMode(value);
  } catch (error) {
    // FALLBACK MECHANISM: Handle the timing issue when setDisasterRecoveryMode is called before provider mounting
    // This catch block implements the fallback mechanism described in the detailed comment above.
    // When AuthEvents listeners trigger setDisasterRecoveryMode during USL authentication (before React renders),
    // we gracefully store the disaster recovery state in sessionStorage instead of crashing the application.
    if (
      error instanceof Error &&
      error.message.includes('DisasterRecoveryProvider not mounted')
    ) {
      console.warn(
        'DisasterRecoveryProvider not yet mounted, storing disaster recovery mode for later application:',
        value,
      );
      // Store the disaster recovery mode to be applied when the provider mounts
      sessionStorage.setItem('pendingDisasterRecoveryMode', value.toString());
      return;
    }
    throw error;
  }
};

export const getDisasterRecoveryMode = () => {
  const context = getDisasterRecoveryContext();
  return context.isInDisasterRecoveryMode;
};
